import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import WeekDaySelector from "../../hoc/DatePicker/Datepicker";
import "./CoachHandleTimes.scss";
import { ROUTE } from "../../shared/enums";
import CoachAvailabilityHistoryPopup from "../../containers/Coaches/CoachAvailabilityHistoryPopup/CoachAvailabilityHistoryPopup";
import useAuth from "../../shared/hooks/useAuth";
import {
  IBooking,
  ICoach,
  ICoachAvailability,
  ICoachBooking,
  ICoachIntervalItem,
  ICourt,
  ICourtType,
  IntervalItem,
} from "../../types/api";
import { getCoachByID, makeCoachAvailable } from "../../api/coaches/coaches";
import {
  IDayColumn,
  IDaySlot,
  actionForState,
  applySpan,
  ISlotRun,
  buildDaySlots,
  groupIntoRuns,
  isSelectableSlot,
  isTimeInPast,
  resolveDayHours,
  selectableBounds,
  spanSummary,
} from "./coachSlotSelection";
import { CognitoUser } from "../../types/cognito";
import { Auth } from "aws-amplify";
import CoachHandleAvailabilityPopup from "../../containers/Coaches/CoachHandleAvailabilityPopup/CoachHandleAvailabilityPopup";
import { combinedForCoachQuery } from "../../api/combinedQueries/combinedForCoachQuery";
import {
  capitalizeWords,
  formatDateToSwedish,
  getMondayOfWeek,
  isDateInCurrentWeek,
} from "../../shared/utility";
import WeekSelector from "../../hoc/DatePicker/Weekpicker";
import CoachShowBookedInfoPopup from "../../containers/Coaches/ CoachShowBookedInfoPopup/ CoachShowBookedInfoPopup";
import { formatToIsoDate } from "../../shared/dateUtils";
import HandleConfirmationPopup from "../../containers/HandleConfirmation/HandleConfirmationPopup";
import { CourtTypeEnum, Weekday } from "../../shared/enums";
import { IFacility } from "../../types/facility";
import Spinner from "../../components/UI/Spinner/Spinner";
import useIsNarrowViewport from "../../shared/hooks/useIsNarrowViewport";

const getWeekdayIndex = (weekday: string): number => {
  const weekdayMap: Record<string, Weekday> = {
    monday: Weekday.Monday,
    tuesday: Weekday.Tuesday,
    wednesday: Weekday.Wednesday,
    thursday: Weekday.Thursday,
    friday: Weekday.Friday,
    saturday: Weekday.Saturday,
    sunday: Weekday.Sunday,
  };
  return weekdayMap[weekday.toLowerCase()] ?? -1; // Return -1 if not found
};

interface CoachHandleTimesProps {}

function CoachHandleTimes(props: CoachHandleTimesProps) {
  // Admins reach this page at /tranare/:coachId to work on someone else's
  // schedule. A coach reaching it without a param gets their own, as before.
  const { coachId } = useParams<{ coachId?: string }>();

  // Who changed a schedule is admin information, not something a coach needs
  // to see about themselves.
  const { isAdmin } = useAuth();
  const [showBookedInfoPopup, setShowBookedInfoPopup] = useState(false);
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [courts, setCourts] = useState<ICourt[]>([]);
  const [coachAvailability, setCoachAvailability] =
    useState<ICoachAvailability[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [
    showCoachHandleAvailabilityPopup,
    setShowCoachHandleAvailabilityPopup,
  ] = useState(false);
  const [facilityOpeningHours, setFacilityOpeningHours] = useState<IFacility>();

  const [coachData, setCoachData] = useState<ICoach>();
  const [bookingToShow, setBookingToShow] = useState<ICoachBooking>();
  const [filterDate, setFilterDate] = useState(() => {
    const storedDate = localStorage.getItem("filterDate");
    return storedDate ? new Date(storedDate) : new Date();
  });
  const [showAvailabilityHistoryPopup, setShowAvailabilityHistoryPopup] =
    useState(false);
  /**
   * Marking is how availability is edited, everywhere. Click a cell to anchor
   * a span, click another to stretch it, confirm to open or close the run in
   * one write.
   *
   * There used to be a separate single-slot flow behind a "Markera fält" mode
   * button, but a one-cell span produces exactly the same write, so it was two
   * code paths for one operation and the mode only stood between the user and
   * the work.
   */
  const [selection, setSelection] = useState<{
    day: string;
    anchor: number;
    focus: number;
  }>();
  const [showSpanConfirm, setShowSpanConfirm] = useState(false);
  // Off by default: a coach opening the page mid-week cares about what is left
  // of it, not the hours that have gone.
  const [showPassed, setShowPassed] = useState(false);

  // Below the stylesheet's own breakpoint the week is a ~1400px canvas seen
  // through a ~390px window, explored on both axes. One day at a time removes
  // the horizontal axis entirely, and costs nothing in behaviour because a
  // span is already locked to a single day.
  const isNarrow = useIsNarrowViewport();
  const [activeDayIndex, setActiveDayIndex] = useState(0);
  // One run open at a time — an accordion, because keeping the day short is
  // the entire reason for collapsing it. Keyed "day#start".
  const [expandedRun, setExpandedRun] = useState<string>();

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((userData) => {
        setUser(userData);
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
      });
  }, []);

  useEffect(() => {
    localStorage.setItem("filterDate", formatToIsoDate(filterDate));
  }, [filterDate]);

  const fetchChosenItems = async ({
    fetchCoachData = false,
    fetchCoachAvailabilityWeekly = false,
    fetchCourts = false,
    fetchFacility = false,
    court_type = "INDOOR" as ICourtType,
  }) => {
    if (!user) return;
    setIsLoading(true);
    try {
      const response = await combinedForCoachQuery({
        member_id: coachId ?? user.attributes["custom:member_id"],
        date: formatToIsoDate(filterDate),
        court_type: court_type,
        fetchCoachData,
        fetchCoachAvailabilityWeekly,
        fetchCourts,
        fetchFacility,
      });
      if (response.courts && fetchCourts && !("message" in response.courts)) {
        setCourts(response.courts);
      }
      if (
        response.facility &&
        fetchFacility &&
        !("message" in response.facility)
      ) {
        setFacilityOpeningHours(response.facility);
      }
      if (
        response.coachData &&
        fetchCoachData &&
        !("message" in response.coachData)
      ) {
        setCoachData(response.coachData);
      }
      if (
        response.coachAvailabilityWeekly &&
        fetchCoachAvailabilityWeekly &&
        !("message" in response.coachAvailabilityWeekly)
      ) {
        setCoachAvailability(response.coachAvailabilityWeekly);
      }
    } catch (error) {
      console.error("Error fetching coach data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // WeekSelector fires onChange once on mount with the current week, which
  // hands back a fresh Date object even when the week has not actually
  // changed. Comparing the week itself stops that from triggering a second
  // fetch on top of the one the page already did.
  const fetchedWeek = useRef<string>();

  useEffect(() => {
    fetchChosenItems({
      fetchCoachData: true,
      fetchCoachAvailabilityWeekly: true,
      fetchCourts: true,
      fetchFacility: true,
    });
    // coachId too: an admin navigating straight from one coach to another
    // keeps this component mounted, and without it the page would keep
    // showing the previous coach.
    // The initial load covers the week that is showing, so record it — the
    // guard below is what stops WeekSelector's mount callback re-fetching it.
    fetchedWeek.current = formatToIsoDate(getMondayOfWeek(filterDate));
  }, [user, coachId]);

  useEffect(() => {
    if (!user) return;
    const week = formatToIsoDate(getMondayOfWeek(filterDate));
    if (fetchedWeek.current === week) return;
    fetchedWeek.current = week;
    fetchChosenItems({ fetchCoachAvailabilityWeekly: true });
  }, [filterDate, user]);
  // A span belongs to one day of one week of one coach; anything that changes
  // what is on screen has to drop it, or a confirm would write to a day the
  // user is no longer looking at.
  useEffect(() => {
    setSelection(undefined);
  }, [filterDate, coachId]);

  // The two layouts group slots differently, so an index from one does not
  // mean the same cell in the other.
  useEffect(() => {
    setSelection(undefined);
  }, [isNarrow]);

  const handleShowBookingInfoPopup = (booking: ICoachBooking) => {
    setBookingToShow(booking);
    setShowBookedInfoPopup(true);
  };

  /**
   * The week as data rather than markup. Marking a span needs to ask which
   * cells neighbour a given cell and what state they are in, which is not a
   * question you can put to a list of JSX elements.
   */
  const weekDays: IDayColumn[] = useMemo(() => {
    if (!facilityOpeningHours || !coachAvailability) return [];
    // Anchored explicitly rather than trusting filterDate to already be a
    // Monday. It is shared with /bokningar through localStorage and can be any
    // weekday, which used to be masked by the week picker forcing it to Monday
    // on mount — the week would otherwise run Thursday to Wednesday.
    const startDate = getMondayOfWeek(new Date(filterDate));
    // One clock for the whole rebuild, so no column is judged against a
    // different moment than its neighbour.
    const now = new Date();

    return Array.from({ length: 7 }).map((_, index) => {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + index);
      const isoDate = formatToIsoDate(date);
      const day = isoDate.split("T")[0];
      const { openingTime, closingTime } = resolveDayHours(
        facilityOpeningHours,
        date,
        isoDate
      );
      const intervals =
        coachAvailability.find((entry) => entry.date === day)?.intervals ?? [];

      return {
        date,
        isoDate,
        day,
        isClosed: !openingTime || !closingTime,
        // Closed days render as an empty column, as before.
        slots:
          openingTime && closingTime
            ? buildDaySlots(
                openingTime,
                closingTime,
                intervals,
                showPassed ? undefined : { day, now }
              )
            : [],
      };
    });
  }, [facilityOpeningHours, coachAvailability, filterDate, showPassed]);

  // Land on today when the week containing it is shown, Monday otherwise.
  useEffect(() => {
    if (!weekDays.length) return;
    const today = formatToIsoDate(new Date()).split("T")[0];
    const index = weekDays.findIndex((column) => column.day === today);
    setActiveDayIndex(index === -1 ? 0 : index);
  }, [weekDays.length && weekDays[0].day]);

  // The visible day and the selected day are the same thing on narrow screens,
  // so paging away from a span has to drop it rather than leave a confirm
  // button pointing at a column that is no longer on screen.
  useEffect(() => {
    setSelection(undefined);
    setExpandedRun(undefined);
  }, [activeDayIndex]);

  // Folding a run away would hide the cells a span is made of, so the span
  // goes with it.
  useEffect(() => {
    setSelection(undefined);
  }, [expandedRun]);

  const visibleDays =
    isNarrow && weekDays.length ? [weekDays[activeDayIndex] ?? weekDays[0]] : weekDays;

  const selectedColumn = selection
    ? weekDays.find((column) => column.day === selection.day)
    : undefined;

  /** How far the current span may stretch — recomputed once, not per cell. */
  const activeBounds = useMemo(() => {
    if (!selection || !selectedColumn) return undefined;
    return selectableBounds(selectedColumn.slots, selection.anchor);
  }, [selection, selectedColumn]);

  const span =
    selection && selectedColumn
      ? spanSummary(selectedColumn.slots, selection.anchor, selection.focus)
      : undefined;

  const spanAction =
    selection && selectedColumn
      ? actionForState(selectedColumn.slots[selection.anchor].state)
      : null;

  /**
   * Cells stay clickable whatever the hour, so you can select a span that has
   * already been and gone. Confirming one is pointless rather than dangerous —
   * nobody can book a time that has started — so it is refused at the point of
   * action and explained there, rather than by making half the grid inert.
   */
  const spanInPast =
    !!span &&
    !!selectedColumn &&
    isTimeInPast(selectedColumn.day, span.startTime, new Date());

  const handleSlotClick = (
    column: IDayColumn,
    slot: IDaySlot,
    index: number
  ) => {
    // A booking is read-only in every mode. It cannot be marked, and its popup
    // is the only place the member's name is shown in full — which is most of
    // why a coach opens this on a phone.
    if (slot.booking) {
      handleShowBookingInfoPopup(slot.booking as IBooking);
      return;
    }

    // Nothing to mark on a slot that has already been and gone.
    if (!isSelectableSlot(slot)) return;

    // First click, or a click in a different day: start over there.
    if (!selection || selection.day !== column.day) {
      setSelection({ day: column.day, anchor: index, focus: index });
      return;
    }
    // Clicking the anchor again clears, which is how you pick a different one.
    if (index === selection.anchor) {
      setSelection(undefined);
      return;
    }
    if (!activeBounds || index < activeBounds.first || index > activeBounds.last)
      return;
    setSelection({ ...selection, focus: index });
  };

  const handleConfirmSpan = async () => {
    if (!selection || !span || !spanAction || !coachData) return;
    // Guarded here as well as in the UI, so neither entry point can write to a
    // time that has already started.
    if (spanInPast) return;
    setShowSpanConfirm(false);
    setIsLoading(true);

    const price = String(coachData.coach_price ?? 0);
    const current =
      coachAvailability?.find((entry) => entry.date === selection.day)
        ?.intervals ?? [];

    const saved = await makeCoachAvailable({
      coach_id: coachData.coach_id,
      date: selection.day,
      // makeCoachAvailable replaces the whole day, so this is the complete new
      // interval list including the untouched bookings.
      intervals: applySpan(
        current,
        span.startTime,
        span.endTime,
        spanAction,
        price
      ).map((interval) => ({
        start_time: interval.start_time,
        end_time: interval.end_time,
        price: String(interval.price ?? price),
        booking: interval.booking ?? undefined,
      })),
    });

    if (saved) {
      toast.success("Tillgänglighet uppdaterad");
      setSelection(undefined);
    }
    // Refetch either way: on failure the grid may be showing stale state, which
    // is what caused the write to be rejected.
    await fetchChosenItems({ fetchCoachAvailabilityWeekly: true });
  };

  /** Shared by cells and by collapsed runs, so the two never drift apart. */
  const stateClass = (state: IDaySlot["state"]) => {
    if (state === "booked") return "slot-booked";
    return state === "available" ? "white" : "not-available";
  };

  /**
   * Narrow screens only. A run of same-state cells shows as one block until
   * tapped; tapping expands it into its half-hours, where the normal single
   * click and the two-tap span both work as before.
   */
  const renderRun = (column: IDayColumn, run: ISlotRun) => {
    // Nothing to fold.
    if (run.slots.length === 1) return renderSlot(column, run.slots[0], run.from);

    const key = `${column.day}#${run.startTime}`;

    if (expandedRun !== key) {
      return (
        <div
          key={key}
          className={`time-item-wrapper text-m slot-run pointer ${stateClass(
            run.state
          )}`}
          onClick={() => setExpandedRun(key)}
        >
          <div className="time-content-wrapper">
            <div>{run.label}</div>
          </div>
          <i className="fa-regular fa-chevron-down slot-run-chevron" />
        </div>
      );
    }

    return (
      <div key={key} className="slot-run-expanded">
        {/* Says which run is open and how to put it back. Deliberately not
            slot-shaped — it is a header, and must not read as a time you
            could click to book or close. */}
        <button
          type="button"
          className="slot-run-header text-m"
          onClick={() => setExpandedRun(undefined)}
        >
          <span className="slot-run-header-label">{run.label}</span>
          <span className="slot-run-header-action text-s">
            Stäng
            <i className="fa-regular fa-chevron-up" />
          </span>
        </button>
        {run.slots.map((slot, offset) =>
          renderSlot(column, slot, run.from + offset)
        )}
      </div>
    );
  };

  const renderSlot = (column: IDayColumn, slot: IDaySlot, index: number) => {
    const isActiveColumn = selection?.day === column.day;
    const inSpan =
      !!selection &&
      isActiveColumn &&
      index >= Math.min(selection.anchor, selection.focus) &&
      index <= Math.max(selection.anchor, selection.focus);

    // Which end you started from decides which way the span can still be
    // stretched, so the anchor is worth telling apart from the rest of it.
    const isAnchor = !!selection && isActiveColumn && index === selection.anchor;

    /** Could this cell join the current span? */
    const markable = selection
      ? isActiveColumn &&
        !!activeBounds &&
        index >= activeBounds.first &&
        index <= activeBounds.last
      : isSelectableSlot(slot);

    // A booking always responds to a click — it opens its popup rather than
    // joining a span — so clickable and markable are not the same question.
    // A cell in another day is clickable too: it starts a new span there.
    const clickable =
      !!slot.booking || markable || (!!selection && !isActiveColumn);

    return (
      <div
        key={`${column.day}-${slot.startTime}`}
        className={[
          "time-item-wrapper text-m",
          stateClass(slot.state),
          clickable ? "pointer" : "",
          inSpan ? "slot-in-span" : "",
          isAnchor ? "slot-anchor" : "",
          // Only ever within the day being marked. On mobile that is the whole
          // grid; on desktop, dimming six of seven columns because someone
          // clicked one cell would be a lot of movement for a single toggle,
          // and the bounds already stop the clicks that would be wrong.
          isActiveColumn && !markable && !inSpan && !slot.booking
            ? "slot-dimmed"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => clickable && handleSlotClick(column, slot, index)}
      >
        <div className="time-content-wrapper">
          <div>{slot.label}</div>
          {slot.booking && (
            <div>{capitalizeWords(slot.booking.member_name || "")}</div>
          )}
        </div>
      </div>
    );
  };

  if (!user || !coachData || !coachAvailability) return <Spinner />;

  return (
    <div className="coach-times-page-container">
      <div className="coach-times-layout">
        <div className="coach-times-declarations">
          <div className="coach-times-title">
            <h1 className="text-l primary-text">
              {coachData.is_hitting_partner ? "Hitting Partner" : "Tränare"} -{" "}
              {capitalizeWords(coachData.first_name)}{" "}
              {capitalizeWords(coachData.last_name)}
            </h1>
            {coachId && (
              // Admins arrive here from /tranare and have no coach nav to get
              // back with.
              <Link to={ROUTE.Coach} className="coach-times-back text-m">
                <i className="fa-regular fa-arrow-left"></i> Tillbaka till
                tränare
              </Link>
            )}
          </div>
          <div className="declarations-wrapper text-m">
            <span className="declaration"> Bokade (Blå)</span>
            <span className="declaration"> Tillgängliga (Vit)</span>
            <span className="declaration"> Icke tillgängliga (Grå)</span>
          </div>
        </div>

        <div className="coach-times-action-wrapper">
          <div className="coach-times-weekday-selector-wrapper">
            <WeekSelector
              value={filterDate}
              onChange={setFilterDate}
              includeWeekdays={false}
              textM
              onWeekChange={(date) => setFilterDate(date)}
            />
            {!isDateInCurrentWeek(filterDate) && (
              <a
                className="text-m"
                onClick={() => {
                  const today = new Date();
                  const monday = getMondayOfWeek(today);
                  setFilterDate(monday);
                }}
              >
                Till denna vecka
              </a>
            )}
            {/* Sits with the date controls: it changes which part of the week
                is on screen, which is the same question the picker answers.
                Hiding is the default, so the label says what turning it on
                reveals rather than what is currently missing. */}
            <div className="coach-show-passed">
              <span className="text-s secondary-text">Visa passerade</span>
              <label className="toggle text-m">
                <input
                  type="checkbox"
                  checked={showPassed}
                  onChange={(e) => {
                    setShowPassed(e.target.checked);
                    // Indices shift when cells appear or disappear, so a span
                    // held across the change would point at the wrong hours.
                    setSelection(undefined);
                    setExpandedRun(undefined);
                  }}
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>
          <div className="coach-availability-action-price-wrapper">
            {/* Only shown once a span exists, so the marking bar does not sit
                there empty. The label says what confirming will do. On narrow
                screens this moves to a fixed bar at the bottom instead — the
                toolbar is far off-screen by the time you have picked a span. */}
            {!isNarrow && span && spanAction && (
              <>
                <button
                  className="text-m span-action"
                  disabled={spanInPast}
                  title={
                    spanInPast
                      ? "Tiden har redan passerat och kan inte ändras"
                      : undefined
                  }
                  onClick={() => setShowSpanConfirm(true)}
                >
                  {spanAction === "open" ? "Öppna" : "Stäng"} {span.label}
                </button>
                <button
                  type="button"
                  className="bar-clear"
                  aria-label="Rensa markering"
                  onClick={() => setSelection(undefined)}
                >
                  <i className="fa-regular fa-xmark" />
                </button>
              </>
            )}
            {isAdmin && (
              <button
                className="text-m"
                onClick={() => setShowAvailabilityHistoryPopup(true)}
              >
                Historik
              </button>
            )}
            {/* Kept on phones as well as desktop. Marking slots covers the
                common case, but the batch editor is still the only way to set a
                whole week in one go, and coaches need that on a phone too. Its
                interval rows wrap below 991px so they fit a narrow screen.
                Only coaches and admins can reach this page at all — the App.tsx
                route forks are the gate, so no role check is needed here. */}
            <button
              className="text-m"
              onClick={() => {
                fetchChosenItems({ fetchCoachAvailabilityWeekly: true });
                setShowCoachHandleAvailabilityPopup(true);
              }}
            >
              Ändra tillgänglighet
            </button>
            <div className="coach-times-prices-wrapper text-m">
              Pris: {coachData.coach_price} (kr/h)
            </div>
          </div>
        </div>
        {/* The seven columns become seven tabs. Each still shows how much of
            the day is open, so paging through the week is not blind. */}
        {isNarrow && (
          <div className="coach-day-strip">
            {weekDays.map((column, index) => {
              const { weekday } = formatDateToSwedish(column.date);
              const openCount = column.slots.filter(
                (slot) => slot.state === "available"
              ).length;
              const bookedCount = column.slots.filter(
                (slot) => slot.state === "booked"
              ).length;
              return (
                <button
                  key={column.day}
                  type="button"
                  className={`coach-day-tab text-s ${
                    index === activeDayIndex ? "active" : ""
                  }`}
                  onClick={() => setActiveDayIndex(index)}
                >
                  <span className="coach-day-tab-name">
                    {weekday.slice(0, 3)}
                  </span>
                  <span className="coach-day-tab-date">
                    {column.day.slice(8, 10)}/{column.day.slice(5, 7)}
                  </span>
                  <span className="coach-day-tab-dots">
                    {openCount > 0 && <i className="dot dot-open" />}
                    {bookedCount > 0 && <i className="dot dot-booked" />}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <div
          className={`coach-table-overflow-wrapper ${
            isNarrow ? "single-day" : ""
          }`}
        >
          <table className="coach-table">
            {!isNarrow && (
              <thead>
                <tr>
                  {weekDays.map((column) => {
                    const { weekday } = formatDateToSwedish(column.date);
                    return (
                      <th key={column.day} className="text-s secondary-text">
                        {weekday} - {column.day.slice(8, 10)}/
                        {column.day.slice(5, 7)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
            )}
            <tbody
              className={`coach-table-body ${isLoading ? "is-loading" : ""}`}
            >
              <tr>
                {visibleDays.map((column) => (
                  <td key={column.day}>
                    {column.slots.length === 0 && (
                      <div className="text-s secondary-text coach-day-empty">
                        {/* Empty because the club was shut, or because
                            everything in it has been and gone. */}
                        {column.isClosed ? "Stängt" : "Passerat"}
                      </div>
                    )}
                    {isNarrow
                      ? groupIntoRuns(column.slots).map((run) =>
                          renderRun(column, run)
                        )
                      : column.slots.map((slot, index) =>
                          renderSlot(column, slot, index)
                        )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {/* Narrow screens only. A day column is around 1900px tall, so by the
          time a span is picked the toolbar is nowhere near the viewport. */}
      {isNarrow && (
        <div className="coach-marking-bar">
          {span && spanAction && selection ? (
            <>
              <span className={`text-s ${spanInPast ? "bar-note" : ""}`}>
                {span.label}
                {spanInPast
                  ? " har redan passerat och kan inte ändras"
                  : // Two taps is not self-evident on touch, where there is no
                    // hover to hint that a second one extends the first.
                    selection.anchor === selection.focus &&
                    " · tryck på en till för att förlänga"}
              </span>
              <div className="coach-marking-bar-actions">
                {/* The bar already states exactly what will happen, and the
                    span cannot touch a booking, so a modal repeating it would
                    be a third tap for something reversible. */}
                {!spanInPast && (
                  <button className="text-m" onClick={handleConfirmSpan}>
                    {spanAction === "open" ? "Öppna" : "Stäng"}
                  </button>
                )}
                <button
                  type="button"
                  className="bar-clear"
                  aria-label="Rensa markering"
                  onClick={() => setSelection(undefined)}
                >
                  <i className="fa-regular fa-xmark" />
                </button>
              </div>
            </>
          ) : (
            <span className="text-s">
              Tryck på en tid för att börja markera
            </span>
          )}
        </div>
      )}
      <CoachAvailabilityHistoryPopup
        showPopup={isAdmin && showAvailabilityHistoryPopup}
        onClose={() => setShowAvailabilityHistoryPopup(false)}
        coachId={coachData.coach_id}
        coachName={`${capitalizeWords(
          coachData.first_name,
        )} ${capitalizeWords(coachData.last_name)}`}
        weekDate={filterDate}
      />
      <CoachShowBookedInfoPopup
        showPopup={showBookedInfoPopup}
        onClose={() => setShowBookedInfoPopup(false)}
        onConfirm={() => {
          fetchChosenItems({ fetchCoachAvailabilityWeekly: true });
          setShowBookedInfoPopup(false);
        }}
        booking={bookingToShow}
        courts={courts}
      />
      <CoachHandleAvailabilityPopup
        showPopup={showCoachHandleAvailabilityPopup}
        onClose={() => setShowCoachHandleAvailabilityPopup(false)}
        onConfirm={() => {
          fetchChosenItems({ fetchCoachAvailabilityWeekly: true });
        }}
        coach={coachData}
        coachAvailability={coachAvailability}
        filterDate={filterDate}
      />
      <HandleConfirmationPopup
        showPopup={showSpanConfirm && !!span && !!spanAction}
        onClose={() => setShowSpanConfirm(false)}
        onConfirm={handleConfirmSpan}
        leftTopElement={
          <span className="text-m">
            {spanAction === "open" ? "Öppna tider" : "Stäng tider"}
          </span>
        }
        // Opening is additive, closing takes bookable time away — so only the
        // second one gets the red button.
        useBlueButton={spanAction === "open"}
        confirmText={spanAction === "open" ? "Öppna tiderna" : "Stäng tiderna"}
        denyText="Avbryt"
        questionText={
          selectedColumn && span
            ? `${
                spanAction === "open" ? "Öppna" : "Stäng"
              } ${span.label} ${formatDateToSwedish(selectedColumn.date)
                .weekday.toLowerCase()} ${
                formatDateToSwedish(selectedColumn.date).dateAndMonth
              }?`
            : ""
        }
      />
    </div>
  );
}
export default CoachHandleTimes;
